import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/feedback/NotificationBell";
import { ErrorBoundary } from "@/components/feedback/ErrorBoundary";
import { useEventStream } from "@/hooks/useEventStream";
import { usePermissions } from "@/hooks/usePermissions";
import { Permission } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar";
import { SidebarFooter } from "./SidebarFooter";
import { ThemeToggle } from "./ThemeToggle";

/**
 * The application shell: a collapsible permission-filtered sidebar, a header
 * hosting the notification bell and theme selector, and the routed content
 * region (README 0.5.4).
 */
export function AppShell() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const { any } = usePermissions();
  const canNotify = any(Permission.NOTIFICATIONS_READ);
  useEventStream(canNotify);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside
        className={cn(
          "flex flex-col border-r bg-card transition-[width] duration-200",
          collapsed ? "w-16" : "w-64",
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b px-3">
          {!collapsed ? (
            <span className="truncate px-1 text-sm font-semibold">{t("common.appName")}</span>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto"
            onClick={() => setCollapsed((value) => !value)}
            aria-label="Toggle sidebar"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>
        <Sidebar collapsed={collapsed} />
        <SidebarFooter collapsed={collapsed} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-end gap-2 border-b bg-card px-4">
          {canNotify ? <NotificationBell /> : null}
          <ThemeToggle />
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
